import React, { useMemo, useState } from 'react';
import { NotificationManager } from 'react-notifications';
import { useSelector } from 'react-redux';

import ModalComponent from '../../../ModalComponent/ModalComponent';
import BrandList from './BrandList';
import Header from './Header';

import '../multiBrandLogin.scss';

import { getStorage, setStorage } from '@/utils/auth-utils';
import { getStorageObject } from '@/utils/global';
import { Mixpanel } from '@/utils/mixpanel';
import { INSERT_DATA_IN_MULTIBRAND_TOKENS_DATA, MULTIBRAND_TOKENS_KEY } from '@/utils/multibrand-utils';
import { ITeamMember } from '@Saral-Influencer-Marketing/saral-utils/src/models';
import * as Sentry from '@sentry/react';
import Gleap from 'gleap';
import Cookies from 'js-cookie';

import { MultibrandTokenDataType } from '@/types/common';
import { ciIdentify } from '@/config/axios-config';

import ButtonsContainer from './ButtonsContainer';

interface Props {
  show: boolean;
  onClose: () => void;
  handleShowAddNewAccount: () => void;
  handleClickRemoveConnectedAccount: (id: string) => void;
  selectedBrand: string | null;
  setSelectedBrand: React.Dispatch<React.SetStateAction<string | null>>;
}

const ChooseAccountModal: React.FC<Props> = ({
  show,
  onClose,
  handleShowAddNewAccount,
  handleClickRemoveConnectedAccount,
  selectedBrand,
  setSelectedBrand,
}) => {
  const { multiBrands, userDetails } = useSelector((state: any) => state.user);

  const [isSwitching, setIsSwitching] = useState(false);

  const handleGleapLogic = (teamMemberId: string, userId: string) => {
    const brandForGleap = multiBrands?.find((brand: any) => brand.connectedBrandUserId === userId);

    const teamMemberForGleap = brandForGleap?.teamMembers?.find((member: ITeamMember) => member._id === teamMemberId);

    Gleap.identify(`${teamMemberId}`, {
      name: `${teamMemberForGleap?.first_name} ${teamMemberForGleap.last_name}`,
      email: teamMemberForGleap?.email,
      plan: teamMemberForGleap?.plan?.name,
    });
  };

  const handleBrandSwitch = async () => {
    setIsSwitching(true);
    const currentToken = getStorage('token');

    if (!currentToken) {
      NotificationManager.error('Invalid Token, Please try again');
      return;
    }

    const currentData = getStorageObject(MULTIBRAND_TOKENS_KEY) || [];

    const currentBrand = currentData?.find((data: MultibrandTokenDataType) => data.teamMemberId === selectedBrand);

    sessionStorage.setItem('performance_filter', JSON.stringify([]));
    INSERT_DATA_IN_MULTIBRAND_TOKENS_DATA({
      token: currentToken,
      userId: userDetails?._id,
      teamMemberId: userDetails?.teamMemberId,
    });

    handleGleapLogic(currentBrand?.teamMemberId, currentBrand?.userId);
    setStorage('token', currentBrand.token);
    Cookies.set('access_token', currentBrand.token, { expires: 15 });

    NotificationManager.success('Account Switched', '', 1000);

    ciIdentify({ id: currentBrand?.userId });

    setTimeout(() => {
      setIsSwitching(false);
      window.location.reload();
    }, 1000);
  };

  const getDisabledStatus = useMemo(() => {
    if (!selectedBrand) return true;
    const currentBrand = multiBrands.find((brand: any) => brand.connectedBrandTeamMemberId === selectedBrand);
    if (currentBrand?.connectedBrandUserId === userDetails?._id) {
      return true;
    } else {
      return false;
    }
  }, [selectedBrand, multiBrands]);

  return (
    <ModalComponent
      show={show}
      onClose={onClose}
      outsideClickClose={false}
      bodyStyle={{ padding: 0 }}
      style={{ padding: 0 }}
      width={432}>
      <Header brandsCount={multiBrands.length} handleShowAddNewAccount={handleShowAddNewAccount} />
      <BrandList
        onSelect={(id: string) => {
          if (multiBrands?.length > 1) {
            setSelectedBrand(id);
          }
        }}
        selectedBrand={selectedBrand}
        handleClickRemoveConnectedAccount={handleClickRemoveConnectedAccount}
      />
      <ButtonsContainer
        isMultiple={multiBrands.length > 1}
        onClose={onClose}
        handleShowAddNewAccount={handleShowAddNewAccount}
        handleBrandSwitch={handleBrandSwitch}
        disabled={getDisabledStatus}
        loading={isSwitching}
      />
    </ModalComponent>
  );
};

export default ChooseAccountModal;
